import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import {
  Button, Grid, TextField, MenuItem, TableContainer, Snackbar,
  SvgIcon, InputAdornment, Autocomplete, Divider, Alert, Box, useMediaQuery, useTheme,
  Skeleton,
  Stack,
  Link,
  Popover,
  CircularProgress,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { SearchSm } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';
import { applyRoundAndFormat, applyRoundAndFormatDecimal, base64ToBlob, standardizeDateFormat, getInventorySku } from '../../../utils';
import { isAutoShipOrder, isOrderTypeDSD, OrderTypeTranslationMap } from '../../../enums/OrderType';
import { client, Category, OrderType, OrderForm, OrderFormUpdate, Supplier, OrderFormItem, OrderFormItemUpdate, OrderFormItemUpdateDate, OrderFormCategory, OrderFormFilterOptions } from '../../../app/services/api/orderManagementClient';
import LeavePageModal from '../../../components/shared/dialogs/LeavePageModal';
import ReloadPageModal from '../../../components/shared/dialogs/ReloadPageModal';
import { Action } from '../../../enums/Action';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { PredictedDemandModel, podClient, ItemConversion } from './../../../app/services/api/podClient';
import { useTranslation } from 'react-i18next';
import './OrderForm.css';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import usePodPredictionReadonlyConfiguration from '../../../hooks/configurations/usePodPredictionReadonlyConfiguration';
import { starbucksFranchises } from '../../../enums/FranchiseNames';
import { isOrderCategoryParent, OrdersCategoriesParentsMap, isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import useLogError from '../../../hooks/useLogError';
import { QuantityWarningDialog } from './../../../components/shared/orders/QuantityWarningDialog';
import { isSameDayOrderForm } from './../utils';
import { isPodEnabledEnvironment } from './../../../app/services/environment';
import VirtualizedTable from './VirtualizedTable';
import { Section } from '../../../components/ui/Section';
import CustomBreadCrumbs from '../../../components/ui/CustomBreadCrumbs';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import { ItemDailyHistory } from '../../../app/services/api/generated';
import { LineChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis, CartesianGrid } from 'recharts';

type UsageSummaryPoint = {
  date: string;
  usage: number;
};

type ItemUsageSummary = {
  points: UsageSummaryPoint[];
  totalUsage14Days: number;
  avgDailyUsage14Days: number;
};

const hasText = (value?: string | null): value is string => typeof value === 'string' && value.trim().length > 0;

const parseNumberLike = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.replace(/,/g, '').trim();
    if (normalizedValue.length === 0) {
      return null;
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
};

const getOrderableConversionCount = (item?: OrderFormItem | null): number | null => {
  if (!item) {
    return null;
  }

  const candidates = [
    parseNumberLike(item.packSize),
    parseNumberLike(item.quantityInUom),
    parseNumberLike(item.caseSize),
  ];

  const conversionCount = candidates.find((candidate) => candidate != null && candidate > 0);
  return conversionCount ?? null;
};

const getUsageValueDisplay = (value: number, item?: OrderFormItem | null) => {
  const primaryUom = hasText(item?.primaryUom) ? item?.primaryUom?.trim() : null;
  const orderableUom = hasText(item?.orderableUom) ? item?.orderableUom?.trim() : null;
  const conversionCount = getOrderableConversionCount(item);
  const primary = primaryUom
    ? `${applyRoundAndFormatDecimal(value)} ${primaryUom}`
    : applyRoundAndFormatDecimal(value);

  if (!orderableUom || conversionCount == null || conversionCount <= 0 || Math.abs(conversionCount - 1) < 0.0001) {
    return {
      primary,
      orderable: null,
    };
  }

  const orderableValue = value / conversionCount;
  const orderable = `${applyRoundAndFormatDecimal(orderableValue)} ${orderableUom}`;

  return {
    primary,
    orderable,
  };
};

const OrderFormPage = () => {

  const navigate = useNavigate();

  const { selectedStore, hasPermissionTo, hasPremiumModuleAccess } = useContext(UserContext);
  const canEditPredictions = hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
  const { t } = useTranslation('orderForm');
  const theme = useTheme();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { getConfiguration, getItemDailyHistory } = useNswagClient();
  const { isPodPredictionReadonlyEnabled, getPodPredictionReadonlyConfiguration } = usePodPredictionReadonlyConfiguration();


  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);

  const [productSearch, setProductSearch] = useState<string>('');
  const [orderType, setOrderType] = useState<number>(-1);
  const [supplier, setSupplier] = useState<string>('');
  const [category, setCategory] = useState<number>(-1);

  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('Name');
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [predictedDemands, setPredictedDemands] = useState<PredictedDemandModel[]>([]);
  const [orderForm, setOrderForm] = useState<OrderForm>();
  const [isPodEnabled, setIsPodEnabled] = useState<boolean>(false);
  const [isUsageSummaryEnabled, setIsUsageSummaryEnabled] = useState<boolean>(false);
  const [usageSummaryAnchorPosition, setUsageSummaryAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [usageSummaryItem, setUsageSummaryItem] = useState<OrderFormItem | null>(null);
  const [usageSummaryCache, setUsageSummaryCache] = useState<Record<string, ItemUsageSummary>>({});
  const [usageSummaryLoadingKey, setUsageSummaryLoadingKey] = useState<string>('');
  const [usageSummaryError, setUsageSummaryError] = useState<string>('');

  const dirtyRef = useRef(false);

  const isLargeScreen = false;
  const isXsScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmallerThan1174px = useMediaQuery('(max-width: 1174px)');

  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');

  const { logError } = useLogError();
  const showImage = !isSmallerThan1174px || isXsScreen;
  const canUseUsageSummary = isUsageSummaryEnabled && hasPermissionTo(['ItemHistoryRead']);
  const usageSummaryPopoverOpen = Boolean(usageSummaryAnchorPosition && usageSummaryItem);
  const usageSummaryDays = 14;
  const isParEnabled = orderForm?.isParEnabled === true;

  const showMessageWhenNoCost = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const [unsavedChangesModal, setUnsavedChangesModal] = useState<{
    open: boolean;
    action: Action;
    actionValue: string;
  }>({
    open: false,
    action: Action.None,
    actionValue: '',
  });

  const [orderFormUpdates, setOrderFormUpdates] = useState<OrderFormUpdate>({
    storeNumber: '',
    orderTypeId: -1,
    supplierNumber: '',
    items: [],
    dates: [],
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const allCategoriesId: number = -1;

  useEffect(() => {
    handleUsageSummaryClose();
    setUsageSummaryCache({});
    setIsUsageSummaryEnabled(false);
    loadData();
    getCurrencyCodeConfiguration();
    getPodPredictionReadonlyConfiguration();
    if (hasPermissionTo(['ItemHistoryRead'])) {
      loadUsageSummaryConfiguration();
    }
    if (isPodEnabledEnvironment()) {
      loadPodStoreConfiguration();
    }
  }, [selectedStore]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/orders-history');
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, navigate]);

  // If POD is enabled, fetch predictions
  useEffect(() => {
    const canLoadPredictions = isPodPredictionReadonlyEnabled || hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
    if (isPodEnabled && hasPremiumModuleAccess('Predictive Ordering Settings') && orderForm?.availableDates && orderForm.availableDates.length > 0 && canLoadPredictions) {
      loadPredictedDemands();
    }
  }, [isPodEnabled, isPodPredictionReadonlyEnabled, orderForm?.categories, orderForm?.availableDates?.map(x => x.deliveryDate).join('|')]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!canUseUsageSummary) {
      handleUsageSummaryClose();
    }
  }, [canUseUsageSummary]);

  const parseBooleanConfiguration = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1';
    }

    return false;
  };

  const getUsageCacheKey = (item: OrderFormItem): string => `${item.id}-${item.sku}`;

  const loadUsageSummaryConfiguration = async () => {
    try {
      const response = await getConfiguration(
        ConfigurationName.OrderPlanningV2Enabled,
        selectedStore?.storeNumber ?? '',
        selectedStore?.franchiseName ?? '',
      ) as { data?: { value?: unknown } };

      setIsUsageSummaryEnabled(parseBooleanConfiguration(response?.data?.value));
    } catch (error) {
      setIsUsageSummaryEnabled(false);
      logError(error);
    }
  };

  const buildUsageSummary = (history: ItemDailyHistory[]): ItemUsageSummary => {
    const today = dayjs();
    const dailyUsageByDate = new Map<string, number>();

    history.forEach((item) => {
      if (!item.date) {
        return;
      }

      const key = dayjs(item.date).format('YYYY-MM-DD');
      dailyUsageByDate.set(key, Number(item.sales ?? 0));
    });

    const points = Array.from({ length: usageSummaryDays }).map((_, index) => {
      const date = today.subtract(usageSummaryDays - 1 - index, 'day').format('YYYY-MM-DD');
      return {
        date,
        usage: dailyUsageByDate.get(date) ?? 0,
      };
    });

    const totalUsage14Days = points.reduce((sum, point) => sum + point.usage, 0);

    return {
      points,
      totalUsage14Days,
      avgDailyUsage14Days: totalUsage14Days / usageSummaryDays,
    };
  };

  const handleUsageSummaryClose = () => {
    setUsageSummaryAnchorPosition(null);
    setUsageSummaryItem(null);
    setUsageSummaryError('');
  };

  const handleUsageSummaryClick = async (event: React.MouseEvent<HTMLElement>, item: OrderFormItem) => {
    if (!canUseUsageSummary) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setUsageSummaryAnchorPosition({
      top: rect.bottom + 8,
      left: rect.left,
    });
    setUsageSummaryItem(item);
    setUsageSummaryError('');

    const cacheKey = getUsageCacheKey(item);
    if (usageSummaryCache[cacheKey]) {
      return;
    }

    if (!selectedStore?.storeNumber) {
      return;
    }

    const fromDate = dayjs().subtract(usageSummaryDays - 1, 'day').format('YYYY-MM-DD');
    const toDate = dayjs().format('YYYY-MM-DD');

    setUsageSummaryLoadingKey(cacheKey);

    try {
      const response = await getItemDailyHistory(item.gtin ?? item.sku, selectedStore.storeNumber, fromDate, toDate);
      const summary = buildUsageSummary(response.data ?? []);

      setUsageSummaryCache((prev) => ({
        ...prev,
        [cacheKey]: summary,
      }));
    } catch (error) {
      logError(error);
      setUsageSummaryError(t('usageSummaryLoadError'));
    } finally {
      setUsageSummaryLoadingKey('');
    }
  };

  const updateOrderFormQuantities = (savedItems: OrderFormItemUpdate[]) => {
    setOrderForm((prevOrderForm) => {
      if (!prevOrderForm) return prevOrderForm;

      let changedAnyCategory = false;
      const updatedCategories = prevOrderForm.categories.map((category) => {
        let changedItems = false;
        const updatedItems = category.items.map((item) => {
          const savedItem = savedItems.find((si) => si.id === item.id);
          if (!savedItem) return item;

          const newQtyArr = item.quantity.map((oldQty, index) => {
            const date = prevOrderForm.availableDates[index].deliveryDate;
            const savedDate = savedItem.dates.find((sd) => sd.date === date);
            return savedDate ? savedDate.quantity : oldQty;
          });

          if (newQtyArr.some((nq, i) => nq !== item.quantity[i])) {
            changedItems = true;
            return { ...item, quantity: newQtyArr };
          }
          return item;
        });

        if (changedItems) {
          changedAnyCategory = true;
          return { ...category, items: updatedItems };
        }
        return category;
      });

      if (!changedAnyCategory) return prevOrderForm;
      return { ...prevOrderForm, categories: updatedCategories };
    });
  };

  const updateSingleOrderFormQuantity = (itemId: number, dateIndex: number, quantity: number | null) => {
    setOrderForm((prevOrderForm) => {
      if (!prevOrderForm) return prevOrderForm;

      const updatedCategories = prevOrderForm.categories.map((cat) => ({
        ...cat,
        items: cat.items.map((it) => {
          if (it.id === itemId) {
            const newQtyArr = [...it.quantity];
            newQtyArr[dateIndex] = quantity;
            return { ...it, quantity: newQtyArr };
          }
          return it;
        }),
      }));

      return { ...prevOrderForm, categories: updatedCategories };
    });
  };

  const loadData = async () => {
    setLoading(true);
    await fetchOrderTypes();
    setLoading(false);
  };

  const clearOrderFormState = () => {
    setOrderTypes([]);
    setOrderType(-1);
    setCategories([]);
    setCategory(allCategoriesId);
    setSuppliers([]);
    setSupplier('');
    setPredictedDemands([]);
    setOrderForm(undefined);
    dirtyRef.current = false;
  };

  const fetchOrderTypes = async () => {
    try {
      const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
      if (orderTypesResponse.success && orderTypesResponse.data.length > 0) {
        const nonAutoshipOrderTypes = orderTypesResponse.data.filter(x => !isAutoShipOrder(x.id));
        setOrderTypes(nonAutoshipOrderTypes);
        const defaultOrderType = nonAutoshipOrderTypes.length > 0 ? nonAutoshipOrderTypes[0].id : -1;
        setOrderType(defaultOrderType);

        const fetchedSuppliers = await fetchSuppliers();
        const selectedSupplierNumber = fetchedSuppliers && fetchedSuppliers.length > 0 && isOrderTypeDSD(defaultOrderType) ? fetchedSuppliers[0].number : '';
        setSupplier(selectedSupplierNumber);

        await fetchCategories(
          defaultOrderType,
          isOrderTypeDSD(defaultOrderType) ? selectedSupplierNumber : '',
        );

        setCategory(allCategoriesId);

        // Fetch order form with the first category
        const options = createOrderFormFilterOptions({
          categoryId: allCategoriesId,
          orderTypeId: defaultOrderType,
          supplierNumber: isOrderTypeDSD(defaultOrderType) ? selectedSupplierNumber : '',
        });
        await fetchOrderForm(options);
      } else {
        clearOrderFormState();
      }
    } catch (error) {
      logError(error);
      clearOrderFormState();
      handleSnackbarOpen(t('fetchOrderError'), 'error');
    }
  };

  const fetchCategories = async (selectedOrderType: number, supplier: string) => {
    try {
      const categories = await client.getCategories(selectedStore?.storeNumber, selectedOrderType, supplier);
      setCategories(categories.data);
      return categories.data.length > 0 ? categories.data[0].id : -1;
    } catch (error) {
      setCategories([]);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
    }
  };

  const fetchSuppliers = async (): Promise<Supplier[] | undefined> => {
    try {
      const res = await client.getSuppliers(selectedStore?.storeNumber);
      setSuppliers(res.data);
      return res.data;
    } catch (error) {
      setSuppliers([]);
      handleSnackbarOpen(t('fetchOrderError') + ' ' + error, 'error');
      return undefined;
    }
  };

  const createOrderFormFilterOptions = (overrides = {}) => {

    const options = {
      storeNumber: selectedStore?.storeNumber,
      orderTypeId: orderType,
      categoryId: category,
      orderBy: orderBy,
      direction: order,
      item: productSearch,
      supplierNumber: isOrderTypeDSD(orderType) ? supplier : '',
    };

    return { ...options, ...overrides };
  };

  const fetchOrderForm = async (options: OrderFormFilterOptions) => {
    try {
      const response = await client.getOrderForm(
        options.storeNumber,
        options.orderTypeId,
        options.categoryId,
        options.orderBy,
        options.direction,
        options.item,
        options.supplierNumber,
      );
      response.data.availableDates = response.data.availableDates.map(item => {
        return {
          ...item,
          deliveryDate: standardizeDateFormat(item.deliveryDate),
        };
      });

      setOrderForm(response.data);
      setPredictedDemands([]);
      dirtyRef.current = false;
    } catch (error) {
      logError(error);
      setPredictedDemands([]);
      setOrderForm(undefined);
      dirtyRef.current = false;
      handleSnackbarOpen(t('fetchOrderError'), 'error');
    }
  };

  const loadPodStoreConfiguration = async () => {
    try {
      if (selectedStore?.franchiseName && selectedStore.storeNumber) {
        const response = await podClient.getStoreConfiguration(selectedStore.franchiseName, selectedStore.storeNumber);
        setIsPodEnabled(response.isPodEnabled);
      }
    } catch (error) {
      handleSnackbarOpen(t('fetchOrderError'), 'error');
    }
  };

  const loadPredictedDemands = async () => {
    try {
      if (selectedStore?.franchiseName && selectedStore.storeNumber && orderForm?.availableDates?.length) {
        const allItems = orderForm.categories.flatMap(x => x.items);
        const itemNumbers = allItems.map(x => getInventorySku(x));
        const conversions: ItemConversion[] = allItems.map(item => ({
          itemNumber: getInventorySku(item),
          conversionNumber: typeof item.packSize === 'string' ? parseFloat(item.packSize) || 1 : item.packSize || 1,
          isFrozen: item.isFrozen || false,
        }));

        const predictions = await podClient.getPredictionsForItems(
          selectedStore.franchiseName,
          selectedStore.storeNumber,
          orderForm.availableDates.map((x) => x.deliveryDate),
          itemNumbers,
          conversions,
        );

        setPredictedDemands(predictions);
      }
    } catch (error) {
      handleSnackbarOpen(t('fetchOrderError'), 'error');
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    const options = createOrderFormFilterOptions();

    await Promise.all([
      fetchCategories(orderType, isOrderTypeDSD(orderType) ? supplier : ''),
      fetchOrderForm(options),
    ]);

    setLoading(false);
  };

  const updatePredictedQuantities = (items: OrderFormItemUpdate[]) => {
    predictedDemands.forEach(x => {
      const originalItem = orderForm?.categories
        .flatMap(category => category.items)
        .find(item => getInventorySku(item) === x.itemNumber);
      const index = items.findIndex(i => i.id === originalItem?.id);
      const currentItem = items.find(i => i.id === originalItem?.id);

      const newDates = orderForm?.availableDates.map((date) => {

        const existingItem = currentItem?.dates.find(i => {
          const itemDate = i.date.split('T')[0];
          return itemDate === date.deliveryDate;
        });

        if (!existingItem) return undefined;
        const predictedDemandItem = x.predictedDemandItems.find(p =>
          isSameDayOrderForm(p.date, date.deliveryDate),
        );
        const predictedQuantity = predictedDemandItem?.hasPrediction ? predictedDemandItem.quantity : undefined;

        const newDate: OrderFormItemUpdateDate = {
          date: date.deliveryDate,
          quantity: existingItem.quantity,
          predictedQuantity: predictedQuantity,
        };
        return newDate;
      }).filter(x => x !== undefined) as OrderFormItemUpdateDate[];

      if (index < 0) {
        return;
      } else {
        items[index].dates = newDates ?? [];
      }
    });
  };

  const handleClearFilters = async () => {
    setProductSearch('');
    setCategory(allCategoriesId);
    setOrder('asc');
    setLoading(true);
    setLoading(false);
  };

  const handleOrderTypeChange = async (changedOrderType: string) => {
    const newOrderType = Number(changedOrderType);
    if (unsavedChangesModal?.open === false && dirtyRef.current && orderType !== newOrderType) {
      setUnsavedChangesModal({ open: true, action: Action.ChangeOrderType, actionValue: newOrderType.toString() });
      return;
    }
    setOrderType(newOrderType);
    setPredictedDemands([]);
    setLoading(true);
    const newSupplier = suppliers && suppliers.length > 0 ? suppliers[0].number : '';
    setSupplier(newSupplier);
    await fetchCategories(newOrderType, isOrderTypeDSD(newOrderType) ? newSupplier : '');

    setCategory(allCategoriesId);
    const options = createOrderFormFilterOptions({
      orderTypeId: newOrderType,
      categoryId: allCategoriesId,
      orderBy: orderBy,
      direction: 'asc',
      item: '',
      supplierNumber: newSupplier,
    });
    await fetchOrderForm(options);
    setLoading(false);
  };

  const canSaveOrder = (): boolean => {
    return !loading && !isAutoShipOrder(orderType);
  };

  const handleSingleQuantityChange = async (
    newQuantity: number | null,
    sku: string,
    itemId: number,
    dateIndex: number,
  ) => {
    try {
      const date = orderForm?.availableDates[dateIndex]?.deliveryDate;
      if (!date) return;

      const singleItemUpdate = {
        id: itemId,
        sku,
        dates: [{ date, quantity: newQuantity }],
      };

      const updates: OrderFormUpdate = {
        storeNumber: selectedStore?.storeNumber ?? '',
        orderTypeId: orderType,
        supplierNumber:
          isOrderTypeDSD(orderType) ? supplier : '',
        items: [singleItemUpdate],
        dates: [date],
      };

      updatePredictedQuantities(updates.items);

      const response = await client.saveOrderForm(
        selectedStore?.storeNumber,
        updates,
      );

      dirtyRef.current = false;

      if (response.success) {
        updateSingleOrderFormQuantity(itemId, dateIndex, newQuantity);
      } else {
        // Handle the error response by setting quantity to minQuantity
        setSnackbar({
          open: true,
          message: response.message || t('autosaveFailed'),
          severity: 'error',
        });
        setShowQuantityWarningDialog(true);
        setWarningMessage(response.message);

        // Find the item to get minQuantity
        const item = orderForm?.categories
          .flatMap((category) => category.items)
          .find((itm) => itm.id === itemId);

        if (item) {
          let correctedQuantity = newQuantity;

          // Determine if the error is due to minQuantity or maxQuantity
          if (newQuantity != null && item.minQuantity != null && newQuantity < item.minQuantity) {
            correctedQuantity = item.minQuantity;
          } else if (newQuantity != null && item.maxQuantity != null && newQuantity > item.maxQuantity) {
            correctedQuantity = item.maxQuantity;
          }

          // update the orderForm to reflect the corrected quantity
          if (correctedQuantity != null) {
            updateSingleOrderFormQuantity(itemId, dateIndex, correctedQuantity);
          }
        } else {
          console.error(
            'Item not found or minQuantity is undefined. Cannot adjust quantity.',
          );
        }
      }
    } catch (error) {
      logError(error);
      setSnackbar({
        open: true,
        message: t('autosaveFailed'),
        severity: 'error',
      });
    }
  };

  const handleSort = async (direction: string) => {
    const isAsc = direction === 'asc';
    setOrder(isAsc ? 'asc' : 'desc');
  };

  const handleOrderBy = async (property: string) => {
    setOrderBy(property);
  };

  const handleDownloadOrderForm = async () => {
    setLoading(true);
    try {
      const result = await client.downloadOrderForm(selectedStore?.storeNumber, orderType, isOrderTypeDSD(orderType) ? supplier : '');
      if (result && result.success) {
        const blob = base64ToBlob(result.data.fileData, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = result.data.fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      handleSnackbarOpen(t('downloadFailed') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      await uploadFile(file);
    } else {
      handleSnackbarOpen(t('noFileSelected'), 'warning');
    }
  };

  const uploadFile = async (file: File) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result?.toString().split(',')[1] ?? '';
        const fileRequest = {
          fileData: base64,
          fileName: file.name,
        };
        setLoading(true);
        const response = await client.uploadOrderForm(
          selectedStore?.storeNumber,
          orderType,
          isOrderTypeDSD(orderType) ? supplier : '',
          fileRequest,
        );
        if (response?.success) {
          handleSnackbarOpen(t('uploadSuccess'), 'success');
          const options = createOrderFormFilterOptions();
          await fetchOrderForm(options);
          setLoading(false);
        } else {
          handleSnackbarOpen(t('uploadFailed'), 'error');
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.onerror = () => {
        handleSnackbarOpen(t('fileReadError'), 'error');
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      handleSnackbarOpen(t('fileUploadError') + ' ' + error, 'error');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  const handleSupplierChange = async (number: string) => {
    setSupplier(number);
    setLoading(true);
    await fetchCategories(orderType, number);
    setCategory(allCategoriesId);
    const options = createOrderFormFilterOptions({
      categoryId: allCategoriesId,
      orderBy: orderBy,
      direction: 'asc',
      item: '',
      supplierNumber: number,
    });
    await fetchOrderForm(options);
    setLoading(false);
  };

  const handleUnsavedChangesModalClose = () => {
    setUnsavedChangesModal({ open: false, action: Action.None, actionValue: '' });
  };

  const handleUnsavedChangesModalConfirm = (action?: Action, actionValue?: string) => {
    if (action) {
      switch (action) {
        case Action.ChangeOrderType:
          if (actionValue) {
            setUnsavedChangesModal({ open: false, action: Action.None, actionValue: '' });
            handleOrderTypeChange(actionValue);
          }
          break;
        default:
          setUnsavedChangesModal({ open: false, action: Action.None, actionValue: '' });
          break;
      }
    }
  };

  const handleMagicWandClicked = async (predictedQuantity: number, item: OrderFormItem, index: number) => {
    await handleSingleQuantityChange(predictedQuantity, item.sku, item.id, index);
  };

  const matchPredictedQuantities = async (dateIndex: number) => {
    try {
      const updatedItems = [...orderFormUpdates.items];
      const date = orderForm?.availableDates[dateIndex];

      if (!date) return;

      predictedDemands.forEach(x => {
        const originalItem = orderForm?.categories
          .flatMap(category => category.items)
          .find(item => item.sku === x.itemNumber);
        const index = updatedItems.findIndex(i => i.id === originalItem?.id);
        const currentItem = updatedItems.find(i => i.id === originalItem?.id);

        const existingItem = currentItem?.dates.find((sd) => sd.date === date.deliveryDate);

        const currentValue = existingItem ? existingItem.quantity : originalItem?.quantity[dateIndex];
        const predictedDemandItem = x.predictedDemandItems.find(p =>
          isSameDayOrderForm(p.date, date.deliveryDate),
        );
        const hasPrediction = predictedDemandItem?.hasPrediction;
        const predictedQuantity = hasPrediction ? predictedDemandItem.quantity : 0;
        const quantity = hasPrediction ? predictedQuantity : currentValue;

        const newDate: OrderFormItemUpdateDate = {
          date: date.deliveryDate,
          quantity: quantity ?? null,
        };

        if (index < 0 && originalItem) {
          const newItem: OrderFormItemUpdate = {
            id: originalItem.id,
            sku: originalItem.sku,
            dates: [newDate],
          };
          updatedItems.push(newItem);
        } else if (index >= 0) {
          updatedItems[index].dates = [newDate];
        }
      });

      const updates: OrderFormUpdate = {
        storeNumber: selectedStore?.storeNumber ?? '',
        orderTypeId: orderType,
        supplierNumber: isOrderTypeDSD(orderType) ? supplier : '',
        items: updatedItems,
        dates: orderForm?.availableDates.map(x => x.deliveryDate) ?? [],
      };

      setLoading(true);

      setOrderFormUpdates(prev => ({
        ...prev,
        items: [],
      }));

      updatePredictedQuantities(updates.items);

      const response = await client.saveOrderForm(selectedStore?.storeNumber, updates);

      updateOrderFormQuantities(updatedItems);

      if (response.success) {
        handleSnackbarOpen(t('saveSuccess'), 'success');
      } else {
        setShowQuantityWarningDialog(true);
        setWarningMessage(response.message);
        fixQuantities();
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('saveFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };


  const quantityError = (item: OrderFormItem, currentValue: string): string | null => {
    const quantity = Number(currentValue);

    if (quantity === 0) {
      return null;
    }

    if ((item.maxQuantity != null) && quantity > item.maxQuantity) {
      return `${t('maxQuantityAllowedIs')}: ${item.maxQuantity}`;
    }

    if ((item.minQuantity != null) && quantity < item.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${item.minQuantity}`;

    return null;
  };

  const isQuantityError = (item: OrderFormItem, currentValue: string): boolean => {
    return quantityError(item, currentValue) != null;
  };

  const getCurrentValue = (item: OrderFormItem, index: number) => {
    const initialQuantity = item.quantity[index];
    const parQuantity = item.parQuantityApplied?.[index] ?? item.calculatedParQuantity ?? null;
    const updatedQuantity = orderFormUpdates.items
      .find(updateItem => updateItem.id === item.id)
      ?.dates.find(d => d.date === orderForm?.availableDates.map(x => x.deliveryDate)[index])?.quantity;
    const currentValue = updatedQuantity != null
      ? updatedQuantity.toString()
      : initialQuantity != null
        ? initialQuantity.toString()
        : isParEnabled && parQuantity != null
          ? parQuantity.toString()
          : isParEnabled
            ? ''
            : '0';
    return currentValue;
  };

  const anyErrors = (): boolean => {
    return orderForm?.availableDates.some((date, index) => checkForErrors(index)) ?? false;
  };

  const checkForErrors = (index: number): boolean => {
    return orderForm?.categories.some(category => checkCategoryErrors(category, index)) ?? false;
  };

  const checkCategoryErrors = (category: OrderFormCategory, index: number): boolean => {
    return category.items.some(item => isQuantityError(item, getCurrentValue(item, index)));
  };

  const fixQuantities = () => {
    setOrderFormUpdates((prev) => {
      const updatedItems = prev.items.map((item) => {
        const adjustedDates = adjustItemQuantities(item);
        return { ...item, dates: adjustedDates };
      });

      return { ...prev, items: updatedItems };
    });
  };

  const adjustItemQuantities = (item: OrderFormItemUpdate) => {
    const matchingItem = findMatchingItem(item.id);
    const maxQuant = matchingItem?.maxQuantity;
    const minQuant = matchingItem?.minQuantity;

    return item.dates.map((date) => adjustDateQuantity(date, maxQuant, minQuant));
  };

  const findMatchingItem = (itemId: number) => {
    const allItems = orderForm?.categories.flatMap(x => x.items);
    return allItems?.find(item => item.id === itemId);
  };

  const adjustDateQuantity = (date: OrderFormItemUpdateDate, maxQuant: number | undefined, minQuant: number | undefined) => {
    let newQuantity = date.quantity;

    if (newQuantity != null && newQuantity !== 0) {
      if (maxQuant && newQuantity > maxQuant) newQuantity = maxQuant;
      if (minQuant && newQuantity < minQuant) newQuantity = minQuant;
    }

    return { ...date, quantity: newQuantity };
  };

  const autocompleteOptions = [{ id: -1, name: 'All', parentName: '' }, ...(Array.isArray(categories) ? categories : [])];

  const getOptionLabel = (option: { parentName?: string; name: string }): string => {
    const parentName = option.parentName;
    const categoryName = option.name;

    const translatedParent = parentName && isOrderCategoryParent(parentName)
      ? t(OrdersCategoriesParentsMap[parentName as keyof typeof OrdersCategoriesParentsMap])
      : parentName;

    const translatedCategory = isOrderCategory(categoryName)
      ? t(OrderCategoriesTranslationMap[categoryName as keyof typeof OrderCategoriesTranslationMap])
      : categoryName;

    if (parentName) {
      return translatedParent
        ? `${translatedParent} > ${translatedCategory}`
        : `${parentName} > ${translatedCategory}`;
    } else {
      return translatedCategory;
    }
  };

  const selectedSupplier = suppliers.find(s => s.number === supplier);
  const minimumOrderValue = isOrderTypeDSD(orderType) ? (selectedSupplier?.minimumOrderValue ?? 0) : 0;

  const displayedDates = useMemo(() => {
    return orderForm?.availableDates ?? [];
  }, [orderForm?.availableDates]);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const usageSummarySelectedKey = usageSummaryItem ? getUsageCacheKey(usageSummaryItem) : '';
  const selectedUsageSummary = usageSummarySelectedKey ? usageSummaryCache[usageSummarySelectedKey] : undefined;
  const isLoadingSelectedUsageSummary = usageSummarySelectedKey !== '' && usageSummaryLoadingKey === usageSummarySelectedKey;
  const avgUsageDisplay = selectedUsageSummary ? getUsageValueDisplay(selectedUsageSummary.avgDailyUsage14Days, usageSummaryItem) : null;
  const totalUsageDisplay = selectedUsageSummary ? getUsageValueDisplay(selectedUsageSummary.totalUsage14Days, usageSummaryItem) : null;

  return (
    <PageArea>
      <Grid item
        xs={12}
        mb={10}>
        <Box>
          <CustomBreadCrumbs title={t('orderDetail')}>
            <Link to={'/store/orders-history'}
              component={RouterLink}>{t('viewOrders')}</Link>
          </CustomBreadCrumbs>
        </Box>
      </Grid>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={4}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('subtitle')}>{t('title')}</PageTitle>
        </Box>
      </Stack>

      <Grid container
        sx={{ marginTop: '6px' }}
        spacing={4}>
        <Grid item
          xs={12}>
          <Section>
            <Grid
              container
              xs={12}>
              <Grid
                item
                container
                alignItems={'center'}
                xs={12}
                spacing={5}
                sx={{ padding: 5 }}
              >
                <Grid item
                  xs={12}
                  md={isOrderTypeDSD(orderType) ? 2 : 4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('orderType')}
                    sx={{ mb: 0 }}
                    value={orderType}
                    disabled={loading}
                    required
                    onChange={(e) => handleOrderTypeChange(e.target.value)}
                    select
                  >
                    {orderTypes?.map((type) => (
                      <MenuItem key={type.id}
                        value={type.id}>
                        {t(OrderTypeTranslationMap[type.name as keyof typeof OrderTypeTranslationMap]) || type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {isOrderTypeDSD(orderType) &&
                  <Grid item
                    md={2}
                    xs={12}>
                    <TextField
                      fullWidth
                      sx={{ mb: 0 }}
                      size='small'
                      label={t('supplier')}
                      disabled={loading}
                      value={supplier}
                      required
                      onChange={(e) => handleSupplierChange(e.target.value)}
                      select
                    >
                      {suppliers.map((type) => (
                        <MenuItem key={type.id}
                          value={type.number}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                }
                <Grid item
                  xs={12}
                  md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    sx={{ mb: 0 }}
                    label={t('orderBy')}
                    disabled={loading}
                    value={orderBy}
                    onChange={(e) => handleOrderBy(e.target.value)}
                    required
                    select
                  >
                    <MenuItem value="Name">{t('productName')}</MenuItem>
                  </TextField>
                </Grid>

                <Grid item
                  xs={12}
                  md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    sx={{ mb: 0 }}
                    label="Direction"
                    disabled={loading}
                    value={order}
                    onChange={(e) => handleSort(e.target.value)}
                    required
                    select
                  >
                    <MenuItem value="asc">{t('ascending')}</MenuItem>
                    <MenuItem value="desc">{t('descending')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    aria-label={t('clearFilters')}
                    fullWidth
                    variant="secondary"
                    disabled={loading}
                    size="lg"
                    onClick={handleClearFilters}
                  >
                    {t('clearFilters')}
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    aria-label={t('downloadOrderForm')}
                    fullWidth
                    variant="secondary"
                    disabled={loading || !canSaveOrder()}
                    startIcon={<DownloadIcon />}
                    size="lg"
                    onClick={handleDownloadOrderForm}
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <span style={{
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    >
                      {t('downloadOrderForm')}
                    </span>
                  </Button>
                </Grid>
                <Grid item
                  xs={12}
                  md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={loading}
                    sx={{ mb: 0 }}
                    placeholder={t('searchByItemNameOrSKU')}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SvgIcon>
                            <SearchSm />
                          </SvgIcon>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={4}>
                  <Autocomplete
                    size="small"
                    options={autocompleteOptions}
                    disabled={loading}
                    sx={{ mb: 0 }}
                    getOptionLabel={getOptionLabel}
                    value={categories.find((cat) => cat.id === category) ?? autocompleteOptions[0]}
                    onChange={(event, newValue) => {
                      setCategory(newValue ? newValue.id : -1);
                    }}
                    renderInput={(params) => (
                      <TextField {...params}
                        sx={{ mb: 0 }}
                        label={t('category')}
                        placeholder={t('choose')} />
                    )}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    fullWidth
                    variant="primary"
                    disabled={loading}
                    size="lg"
                    startIcon={<SearchSm />}
                    onClick={handleSearch}
                  >
                    {t('search')}
                  </Button>
                </Grid>

                <Grid item
                  xs={12}
                  md={2}>
                  <Button
                    aria-label={t('uploadOrderForm')}
                    fullWidth
                    variant="secondary"
                    disabled={loading || !canSaveOrder()}
                    startIcon={<UploadIcon />}
                    size="lg"
                    onClick={handleUploadButtonClick}
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <span style={{
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    >
                      {t('uploadOrderForm')}
                    </span>
                  </Button>
                  <input aria-hidden
                    type="file"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".xlsx, .xls"
                  />
                </Grid>
                {isOrderTypeDSD(orderType) && minimumOrderValue > 0 && (
                  <Grid item
                    md={2}
                    xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('minimumOrderValue')}
                      sx={{ mb: 0 }}
                      disabled
                      value={applyRoundAndFormat(minimumOrderValue, currencyCode)}
                    />
                  </Grid>
                )}
              </Grid>

              <Grid item
                xs={12}>
                <Divider />
              </Grid>
              {isParEnabled && (
                <Grid item
                  xs={12}>
                  <Alert severity="info"
                    sx={{ mb:5, mt: 5 }}>
                    {t('parInfoBanner')}
                  </Alert>
                </Grid>
              )}

              <Grid item
                xs={12}>
                {loading ? (
                  <Skeleton variant="rectangular"
                    width={'100%'}
                    height={'max(95vh, 400px)'} />
                ) : (
                  <TableContainer component="div">
                  </TableContainer>
                )}
              </Grid>
            </Grid>
            <VirtualizedTable
              loading={loading}
              showImage={showImage}
              orderForm={orderForm}
              orderType={orderType}
              displayedDates={displayedDates}
              predictedDemands={predictedDemands}
              matchPredictedQuantities={matchPredictedQuantities}
              handleMagicWandClicked={handleMagicWandClicked}
              orderFormUpdates={orderFormUpdates}
              handleSingleQuantityChange={handleSingleQuantityChange}
              canSaveOrder={canSaveOrder()}
              canEditPredictions={canEditPredictions}
              isQuantityError={isQuantityError}
              quantityError={quantityError}
              showMessageWhenNoCost={showMessageWhenNoCost}
              currencyCode={currencyCode ?? ''}
              t={t}
              isLargeScreen={isLargeScreen}
              usePagination={false}
              page={0}
              totalPages={1}
              handlePageChange={() => { }}
              dirtyRef={dirtyRef}
              minimumOrderValue={minimumOrderValue}
              canViewUsageSummary={canUseUsageSummary}
              isEnhancedCutoffEnabled={isUsageSummaryEnabled}
              onUsageSummaryClick={handleUsageSummaryClick}
              isParEnabled={isParEnabled}
            />
            <Box sx={{
              height: '12px',
              borderTop: '1px solid',
              borderColor: theme.palette.custom.gray[300],
            }}></Box>
          </Section>
        </Grid>
      </Grid>

      <Popover
        open={usageSummaryPopoverOpen}
        anchorReference="anchorPosition"
        anchorPosition={usageSummaryAnchorPosition ?? undefined}
        onClose={handleUsageSummaryClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: theme.palette.custom.gray[300],
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 4, width: isMobile ? 360 : 520 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="textSM"
              fontWeight={700}>
              {usageSummaryItem?.name ?? t('usageSummaryTitle')}
            </Typography>
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ mt: 0.5 }}>
              {t('usageSummaryWindow', { days: usageSummaryDays })}
            </Typography>
          </Box>

          {isLoadingSelectedUsageSummary && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!isLoadingSelectedUsageSummary && usageSummaryError !== '' && (
            <Typography variant="textSM"
              color="error.main"
              sx={{ mt: 4 }}>
              {usageSummaryError}
            </Typography>
          )}

          {!isLoadingSelectedUsageSummary && usageSummaryError === '' && selectedUsageSummary && (
            <>
              <Stack direction="row"
                spacing={2}
                sx={{ mt: 2.5 }}>
                <Box sx={{ flex: 1, display: 'flex', p: 4, justifyContent: 'space-between', backgroundColor: theme.palette.custom.gray[100], borderRadius: 2, px: 2.5, py: 1.75 }}>
                  <Typography variant="textXS"
                    color="text.secondary">
                    {t('usageAverageDaily')}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="textXS"
                      sx={{ display: 'block' }}>
                      {avgUsageDisplay?.primary}
                    </Typography>
                    {avgUsageDisplay?.orderable && (
                      <Typography variant="textXS"
                        sx={{ display: 'block' }}>
                        {avgUsageDisplay.orderable}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ flex: 1, display: 'flex', p: 4, justifyContent: 'space-between', backgroundColor: theme.palette.custom.gray[100], borderRadius: 2, px: 2.5, py: 1.75 }}>
                  <Typography variant="textXS"
                    color="text.secondary">
                    {t('usageTotal14Days')}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="textXS"
                      sx={{ display: 'block' }}>
                      {totalUsageDisplay?.primary}
                    </Typography>
                    {totalUsageDisplay?.orderable && (
                      <Typography variant="textXS"
                        sx={{ display: 'block' }}>
                        {totalUsageDisplay.orderable}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Stack>

              <Box sx={{ mt: 2.5, height: 240 }}>
                <ResponsiveContainer width="100%"
                  height="100%">
                  <LineChart data={selectedUsageSummary.points}
                    margin={{ top: 12, right: 16, left: 0, bottom: 12 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={theme.palette.divider}
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value: string) => dayjs(value).format('DD MMM')}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 11,
                      }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                      minTickGap={16}
                    />
                    <YAxis
                      tickFormatter={(value: number) => Math.round(value).toString()}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 11,
                      }}
                      tickLine={false}
                      axisLine={{ stroke: theme.palette.divider }}
                      width={44}
                    />
                    <RechartsTooltip
                      labelFormatter={(value: string) => dayjs(value).format('DD MMM')}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) {
                          return null;
                        }

                        const rawValue = typeof payload[0].value === 'number'
                          ? payload[0].value
                          : Number(payload[0].value ?? 0);

                        const usageDisplay = getUsageValueDisplay(rawValue, usageSummaryItem);

                        return (
                          <Box
                            sx={{
                              borderRadius: 3,
                              border: '1px solid',
                              borderColor: theme.palette.custom.gray[300],
                              backgroundColor: theme.palette.common.white,
                              boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                              px: 2,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="textXS"
                              sx={{ mb: 0.75 }}
                            >
                              {t('usageLabel')} {typeof label === 'string' ? dayjs(label).format('DD MMM') : ''}
                            </Typography>
                            <Box>
                              <Typography
                                variant="textXS"
                                sx={{ lineHeight: 1.35, display: 'block' }}
                              >
                                {usageDisplay.primary}
                              </Typography>
                              {usageDisplay.orderable && (
                                <Typography
                                  variant="textXS"
                                  sx={{ pt: 0.25, lineHeight: 1.35, display: 'block' }}
                                >
                                  {usageDisplay.orderable}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      }}
                      contentStyle={{
                        borderRadius: 12,
                        borderColor: theme.palette.custom.gray[300],
                        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="usage"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2.5}
                      dot={{ r: 2, strokeWidth: 0, fill: theme.palette.primary.main }}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              <Typography variant="textXS"
                color="text.secondary"
                sx={{ mt: 1.5 }}>
                {t('usageSummaryHint')}
              </Typography>
            </>
          )}
        </Box>
      </Popover>

      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={snackbar.message === t('autosaving') ? null : 2000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ReloadPageModal
        open={unsavedChangesModal?.open ?? false}
        onConfirm={() => handleUnsavedChangesModalConfirm(unsavedChangesModal?.action, unsavedChangesModal?.actionValue)}
        onClose={handleUnsavedChangesModalClose}
      />

      <QuantityWarningDialog
        setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog}
      />
    </PageArea >
  );
};

export default OrderFormPage;